import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Connects to data-controller="title-split"
//
// Loads a multi-page title PDF, renders page thumbnails, and lets the user
// pick — independently per page — a title stage (Vendor Copy / Original Title /
// Customer Title) and a truck. "Mark all that are unselected as <stage>"
// buttons let the user pre-fill a stage for every still-empty page in one
// click.
//
// The form is the source of truth for per-page picks: each cloned <select>
// gets `name="pages[N][deal_id]"` and the radios get `name="pages[N][stage]"`.
// On submit, this controller direct-uploads the original PDF and one split
// PDF per (deal, stage) group, then stamps a `pages[N][signed_id]` hidden
// input onto each page card in the group. The form's natural serialization
// then carries everything to the server, which groups by (deal, stage) and
// upserts one Title per group.
//
// A truck without a stage blocks submit (we'd have nothing to do with it).
// A stage without a truck is silently ignored — useful when the user pre-
// selects "all customer title" but only assigns trucks to a few pages.
//
// PDF libraries (pdfjs-dist ~1.6MB, pdf-lib ~300KB) are loaded lazily on first
// use so they don't bloat the main bundle for every page.
export default class extends Controller {
  static targets = [
    "form",
    "pagesContainer",
    "submitBtn",
    "summary",
    "fallbackPicker",
    "originalPdfInput",
    "loadingIndicator",
    "dealSelectTemplate",
    "thumbnailControlsTemplate",
    "bulkActions"
  ]

  static values = {
    workerUrl: String
  }

  // Lazily load PDF libraries on first use. Returns { pdfjsLib, PDFDocument }.
  async loadPdfLibraries() {
    if (!this._pdfLibs) {
      const [pdfjs, pdfLib] = await Promise.all([
        import("pdfjs-dist"),
        import("pdf-lib")
      ])
      pdfjs.GlobalWorkerOptions.workerSrc = this.workerUrlValue
      this._pdfLibs = { pdfjsLib: pdfjs, PDFDocument: pdfLib.PDFDocument }
    }
    return this._pdfLibs
  }

  async connect() {
    if (window.titlePdfFile) {
      this.file = window.titlePdfFile
      window.titlePdfFile = null
      this.pdfBytes = new Uint8Array(await this.file.arrayBuffer())
      this.loadPdf()
    } else {
      this.fallbackPickerTarget.classList.remove("hidden")
    }
  }

  async fallbackFileSelected(event) {
    const file = event.target.files[0]
    if (!file) return

    if (file.type !== "application/pdf") {
      this.showToast("error", "Please select a PDF file.")
      return
    }

    const maxSize = 25 * 1024 * 1024
    if (file.size > maxSize) {
      this.showToast("error", "File is too large. Maximum size is 25MB.")
      return
    }

    this.file = file
    this.pdfBytes = new Uint8Array(await file.arrayBuffer())
    this.fallbackPickerTarget.classList.add("hidden")
    this.loadPdf()
  }

  async loadPdf() {
    this.showLoading(true)

    try {
      const { pdfjsLib } = await this.loadPdfLibraries()
      this.pdfDoc = await pdfjsLib.getDocument({ data: this.pdfBytes.slice() }).promise
      await this.renderPages()
    } catch (error) {
      console.error("Failed to load PDF:", error)
      this.showToast("error", "Could not read PDF — the file may be corrupt or password-protected.")
      this.fallbackPickerTarget.classList.remove("hidden")
    } finally {
      this.showLoading(false)
    }
  }

  async renderPages() {
    const totalPages = this.pdfDoc.numPages
    this.pagesContainerTarget.innerHTML = ""

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      const page = await this.pdfDoc.getPage(pageNum)

      // Render at high resolution for readability, display at CSS size.
      // Use devicePixelRatio (typically 2x on Retina) with a floor of 2
      // so even on 1x screens the scanned title text is legible.
      const dpr = Math.max(window.devicePixelRatio || 1, 2)
      const renderWidth = 600
      const unscaledViewport = page.getViewport({ scale: 1 })
      const scale = (renderWidth * dpr) / unscaledViewport.width
      const viewport = page.getViewport({ scale })

      // Build collapsible page card. Uses DaisyUI's <details>-based collapse
      // component so we get click-on-summary + keyboard toggling for free.
      // Each card auto-collapses the first time both a stage and a truck
      // are picked, then stays under the user's manual control.
      const card = document.createElement("details")
      card.className = "collapse collapse-arrow bg-base-100 shadow-sm border border-base-300"
      card.open = true
      card.dataset.pageNum = pageNum
      // Per-card zoom state — each thumbnail tracks its own zoom factor so
      // users can zoom in on the one ambiguous page without affecting the rest.
      card.dataset.thumbnailZoom = "1"

      // Summary row: page number on the left, live assignment summary on
      // the right. The right-side text updates as the user picks stage/
      // truck and is what they see when the card is collapsed.
      const summary = document.createElement("summary")
      summary.className = "collapse-title text-sm font-semibold"
      summary.innerHTML = `
        <div class="flex items-baseline justify-between gap-3 pr-6">
          <span>Page ${pageNum} of ${totalPages}</span>
          <span class="text-base-content/60 font-normal" data-card-summary></span>
        </div>
      `

      const cardBody = document.createElement("div")
      cardBody.className = "collapse-content space-y-2"

      // Header: zoom toolbar (page label has moved to the <summary>).
      const header = this.thumbnailControlsTemplateTarget.content.cloneNode(true).firstElementChild

      // Canvas thumbnail wrapped in a fixed-aspect scroll container. The
      // wrapper's height is locked to the page's natural aspect ratio so the
      // card stays the same size at every zoom level — zooming past 100%
      // scrolls *inside* the card (both axes) instead of pushing the deal/
      // stage controls down the page (issue #136 follow-up).
      const scrollWrapper = document.createElement("div")
      scrollWrapper.className = "overflow-auto border border-base-300 rounded"
      scrollWrapper.style.aspectRatio = `${unscaledViewport.width} / ${unscaledViewport.height}`

      const zoomWrapper = document.createElement("div")
      zoomWrapper.dataset.thumbnailZoomWrapper = ""
      zoomWrapper.style.width = "100%"

      const canvas = document.createElement("canvas")
      canvas.width = viewport.width
      canvas.height = viewport.height
      canvas.className = "block w-full h-auto select-none"
      canvas.style.aspectRatio = `${unscaledViewport.width} / ${unscaledViewport.height}`

      const ctx = canvas.getContext("2d")
      await page.render({ canvasContext: ctx, viewport }).promise

      zoomWrapper.appendChild(canvas)
      scrollWrapper.appendChild(zoomWrapper)

      // Drag-to-pan inside the card: when zoomed past 100% the canvas
      // overflows its scroll wrapper, and the user can grab it to scroll
      // the wrapper instead of fishing for the scrollbar.
      this._wireDragPan(card, canvas, scrollWrapper)

      // Per-page controls (stage radio + truck select) cloned from server
      // template. Stamping the real Rails form names per-page does double duty:
      // (1) the form serializes them naturally on submit and (2) the shared
      // per-page name on the radios groups them for mutual exclusion.
      const controls = this.dealSelectTemplateTarget.content.cloneNode(true)
      controls.querySelector("select").name = `pages[${pageNum}][deal_id]`
      controls.querySelectorAll('input[type="radio"]').forEach(r => {
        r.name = `pages[${pageNum}][stage]`
      })

      cardBody.appendChild(header)
      cardBody.appendChild(scrollWrapper)
      cardBody.appendChild(controls)
      card.appendChild(summary)
      card.appendChild(cardBody)
      this.pagesContainerTarget.appendChild(card)
    }

    this.bulkActionsTarget.classList.remove("hidden")
    this.updateSummary()
  }

  // ---------------------------------------------------------------------------
  // Per-thumbnail zoom — lightweight CSS-based zoom on each preview card so
  // the +/− buttons are discoverable without opening the full-screen dialog.
  // The canvas is rendered once at high DPR (see renderPages above), so CSS
  // scaling up to ~3x stays sharp without any pdfjs re-render.
  // ---------------------------------------------------------------------------

  thumbnailZoomIn(event) { this._stepThumbnailZoom(event.currentTarget, 1.25) }
  thumbnailZoomOut(event) { this._stepThumbnailZoom(event.currentTarget, 1 / 1.25) }

  thumbnailZoomReset(event) {
    const card = event.currentTarget.closest("[data-page-num]")
    this._setThumbnailZoom(card, 1)
  }

  // The expand-icon button — opens the full-screen viewer for this card's
  // page. Equivalent to clicking the canvas itself but easier to discover.
  thumbnailOpenZoom(event) {
    const card = event.currentTarget.closest("[data-page-num]")
    this.openZoom(Number(card.dataset.pageNum))
  }

  _stepThumbnailZoom(button, factor) {
    const card = button.closest("[data-page-num]")
    const current = parseFloat(card.dataset.thumbnailZoom) || 1
    this._setThumbnailZoom(card, current * factor)
  }

  _setThumbnailZoom(card, zoom) {
    const clamped = Math.max(0.5, Math.min(4, zoom))
    card.dataset.thumbnailZoom = String(clamped)
    const wrapper = card.querySelector("[data-thumbnail-zoom-wrapper]")
    if (wrapper) wrapper.style.width = `${clamped * 100}%`
    const label = card.querySelector("[data-thumbnail-zoom-level]")
    if (label) label.textContent = `${Math.round(clamped * 100)}%`
    // Re-evaluate the grab/default cursor once the new layout has settled.
    if (card.__updateThumbnailCursor) {
      requestAnimationFrame(card.__updateThumbnailCursor)
    }
  }

  // Wire pointer events on a thumbnail canvas so dragging it scrolls the
  // wrapping scroll container. Uses Pointer Events with setPointerCapture so
  // we don't need to attach document-level mousemove/mouseup listeners per
  // card. The cursor reflects state: `grab` when there's something to pan,
  // `grabbing` mid-drag, and `default` when the page already fits.
  _wireDragPan(card, canvas, scrollWrapper) {
    let isPanning = false
    let panStartX, panStartY, scrollStartX, scrollStartY

    const hasOverflow = () =>
      scrollWrapper.scrollWidth > scrollWrapper.clientWidth + 1 ||
      scrollWrapper.scrollHeight > scrollWrapper.clientHeight + 1

    const updateCursor = () => {
      canvas.style.cursor = isPanning
        ? "grabbing"
        : (hasOverflow() ? "grab" : "default")
    }

    canvas.addEventListener("pointerdown", (event) => {
      if (event.button !== 0 || !hasOverflow()) return
      canvas.setPointerCapture(event.pointerId)
      isPanning = true
      panStartX = event.clientX
      panStartY = event.clientY
      scrollStartX = scrollWrapper.scrollLeft
      scrollStartY = scrollWrapper.scrollTop
      updateCursor()
      event.preventDefault()
    })

    canvas.addEventListener("pointermove", (event) => {
      if (!isPanning) return
      scrollWrapper.scrollLeft = scrollStartX - (event.clientX - panStartX)
      scrollWrapper.scrollTop = scrollStartY - (event.clientY - panStartY)
    })

    const endPan = (event) => {
      if (!isPanning) return
      isPanning = false
      try { canvas.releasePointerCapture(event.pointerId) } catch (_) { /* ignore */ }
      updateCursor()
    }
    canvas.addEventListener("pointerup", endPan)
    canvas.addEventListener("pointercancel", endPan)

    // Stash the cursor updater on the card so _setThumbnailZoom can re-run
    // it after layout reflows (zoom 100% → 200% should switch to `grab`).
    card.__updateThumbnailCursor = updateCursor
    requestAnimationFrame(updateCursor)
  }

  // Set the given stage on every page card that doesn't already have a stage
  // picked. Pages with an existing stage selection are left alone, so users
  // can fine-tune individual pages and then bulk-fill the rest. Cards that
  // already had a truck picked will auto-collapse here too.
  markAllStage(event) {
    const stage = event.currentTarget.dataset.stage
    for (const card of this.pagesContainerTarget.querySelectorAll("[data-page-num]")) {
      if (card.querySelector('input[type="radio"]:checked')) continue
      const radio = card.querySelector(`input[type="radio"][value="${stage}"]`)
      if (radio) {
        radio.checked = true
        this._updateCardSummary(card)
        this._maybeAutoCollapse(card)
      }
    }
    this.updateSummary()
  }

  // Update the assignment summary text shown in a card's <summary> row —
  // visible both when the card is expanded and when it's collapsed, so the
  // user can see at a glance what each page got assigned to.
  _updateCardSummary(card) {
    const summaryEl = card.querySelector("[data-card-summary]")
    if (!summaryEl) return
    const dealId = card.querySelector("select").value
    const stageRadio = card.querySelector('input[type="radio"]:checked')

    if (!dealId && !stageRadio) {
      // Don't clutter the summary row until the user has picked something.
      summaryEl.textContent = ""
      return
    }

    const parts = []
    if (stageRadio) parts.push(stageRadio.getAttribute("aria-label"))
    if (dealId) {
      const opt = card.querySelector(`select option[value="${dealId}"]`)
      if (opt) parts.push(opt.textContent.trim())
    }
    summaryEl.textContent = parts.join(" · ")
  }

  // Auto-collapse a card the first time both a stage and a truck are picked,
  // freeing up screen real estate so the next un-assigned page is closer to
  // the top of the viewport. We only do this once per card — if the user
  // re-opens to tweak, we leave it alone from then on.
  _maybeAutoCollapse(card) {
    if (card.dataset.autoCollapsed === "true") return
    const dealId = card.querySelector("select").value
    const stage = card.querySelector('input[type="radio"]:checked')
    if (!dealId || !stage) return
    card.open = false
    card.dataset.autoCollapsed = "true"
  }

  // Read the per-page selections from the form's natural serialization.
  // Returns one entry per rendered page, in page order, with either field
  // possibly null when the user hasn't picked yet.
  pageSelections() {
    const fd = new FormData(this.formTarget)
    const totalPages = this.pdfDoc ? this.pdfDoc.numPages : 0
    return Array.from({length: totalPages}, (_, i) => {
      const pageNum = i + 1
      return {
        pageNum,
        dealId: fd.get(`pages[${pageNum}][deal_id]`) || null,
        stage: fd.get(`pages[${pageNum}][stage]`) || null
      }
    })
  }

  // Either change handler updates the per-card summary text, considers an
  // auto-collapse, and then re-derives the global summary from the DOM.
  assignmentChanged(event) {
    const card = event.target.closest("[data-page-num]")
    if (card) {
      this._updateCardSummary(card)
      this._maybeAutoCollapse(card)
    }
    this.updateSummary()
  }

  stageChanged(event) {
    const card = event.target.closest("[data-page-num]")
    if (card) {
      this._updateCardSummary(card)
      this._maybeAutoCollapse(card)
    }
    this.updateSummary()
  }

  updateSummary() {
    const totalPages = this.pdfDoc ? this.pdfDoc.numPages : 0
    const selections = this.pageSelections()
    const assignedCount = selections.filter(s => s.dealId && s.stage).length
    // Truck without stage is the only invalid combination — stage without
    // truck just means "don't include this page" and is fine.
    const truckMissingStageCount = selections.filter(s => s.dealId && !s.stage).length

    let summaryText = `${assignedCount}/${totalPages} pages assigned`
    if (truckMissingStageCount > 0) {
      const noun = truckMissingStageCount === 1 ? "page is" : "pages are"
      summaryText += ` — ${truckMissingStageCount} ${noun} missing a stage`
    }
    this.summaryTarget.textContent = summaryText

    this.submitBtnTarget.disabled = assignedCount === 0 || truckMissingStageCount > 0

    // Highlight a bulk button when every page already shares its stage — gives
    // immediate visual feedback on click and reflects per-page selections too.
    // `btn-primary` matches the green-fill the per-page radios use when checked.
    for (const btn of this.bulkActionsTarget.querySelectorAll("button[data-stage]")) {
      const allMatch = selections.length > 0 && selections.every(s => s.stage === btn.dataset.stage)
      btn.classList.toggle("btn-primary", allMatch)
    }
  }

  async submit(event) {
    event.preventDefault()
    if (this.submitBtnTarget.disabled) return

    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.innerHTML = '<span class="loading loading-spinner loading-sm"></span> Uploading...'

    try {
      // Direct-upload the original combined PDF
      this.originalPdfInputTarget.value = await this.directUpload(this.file)

      // Group fully-assigned page cards by `${stage}::${dealId}`. The cards
      // already carry the user's deal/stage picks via their <select>/<input>
      // values — no separate state to consult.
      const groups = new Map()
      for (const card of this.pagesContainerTarget.querySelectorAll("[data-page-num]")) {
        const dealId = card.querySelector("select").value
        const stage = card.querySelector('input[type="radio"]:checked')?.value
        if (!dealId || !stage) continue
        const key = `${stage}::${dealId}`
        if (!groups.has(key)) groups.set(key, [])
        groups.get(key).push(card)
      }

      // Build + direct-upload one combined PDF per group, then stamp the
      // resulting signed_id onto every page card in that group as a hidden
      // input. The form's natural serialization sends pages[N][signed_id]
      // alongside the existing pages[N][deal_id] and pages[N][stage] fields.
      const { PDFDocument } = await this.loadPdfLibraries()
      for (const cards of groups.values()) {
        const pageNums = cards.map(c => Number(c.dataset.pageNum))
        const splitPdf = await PDFDocument.create()
        const srcDoc = await PDFDocument.load(this.pdfBytes)
        const copiedPages = await splitPdf.copyPages(srcDoc, pageNums.map(p => p - 1))
        copiedPages.forEach(p => splitPdf.addPage(p))
        const file = new File(
          [await splitPdf.save()],
          `title_pages_${pageNums.join("-")}.pdf`,
          { type: "application/pdf" }
        )
        const signedId = await this.directUpload(file)
        for (const card of cards) {
          this.appendSignedIdInput(card, signedId)
        }
      }

      this.submitBtnTarget.innerHTML = '<span class="loading loading-spinner loading-sm"></span> Saving...'
      this.formTarget.requestSubmit()
    } catch (error) {
      console.error("Failed to upload PDF:", error)
      this.showToast("error", "Failed to upload PDF. Please try again.")
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.innerHTML = "Assign Titles"
    }
  }

  appendSignedIdInput(card, signedId) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = `pages[${card.dataset.pageNum}][signed_id]`
    input.value = signedId
    card.appendChild(input)
  }

  async openZoom(pageNum) {
    const page = await this.pdfDoc.getPage(pageNum)
    const zoomController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller~="pdf-zoom"]'),
      "pdf-zoom"
    )
    if (zoomController) {
      zoomController.open(page, pageNum, this.pdfDoc.numPages)
    }
  }

  directUpload(file) {
    const url = "/rails/active_storage/direct_uploads"
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, url)
      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob.signed_id)
        }
      })
    })
  }

  showLoading(show) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle("hidden", !show)
    }
  }

  showToast(type, message) {
    document.dispatchEvent(new CustomEvent("toast:show", {
      detail: { type, message }
    }))
  }
}
